import type { Position, SignalState, PortfolioSnapshot } from '../lib/supabase'

interface Props {
  positions: Position[]
  signals: SignalState[]
  snapshot: PortfolioSnapshot | null
}

const SLEEVES = [
  { key: 'etf',   label: 'ETF',   color: 'var(--sleeve-etf)',   exposureKey: 'etf_exposure_pct'   as const },
  { key: 'stock', label: 'Stock', color: 'var(--sleeve-stock)', exposureKey: 'stock_exposure_pct' as const },
  { key: 'ipo',   label: 'IPO',   color: 'var(--sleeve-ipo)',   exposureKey: 'ipo_exposure_pct'   as const },
] as const

function fmt$(v: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v)
}

function fmtPct(v: number, sign = false): string {
  return `${sign && v >= 0 ? '+' : ''}${v.toFixed(2)}%`
}

export function LiveAnalysis({ positions, signals, snapshot }: Props) {
  // Latest signal per ticker
  const latestSignals = signals.reduce((acc, s) => {
    if (!acc[s.ticker] || s.date > acc[s.ticker].date) acc[s.ticker] = s
    return acc
  }, {} as Record<string, SignalState>)

  const totalSignals    = Object.values(latestSignals).length
  const totalQualified  = Object.values(latestSignals).filter(s => s.trend_qual).length
  const healthPct       = totalSignals > 0 ? totalQualified / totalSignals : 0
  const healthColor     = healthPct > 0.6 ? 'var(--positive)' : healthPct > 0.3 ? 'var(--warning)' : 'var(--negative)'

  const sleeveData = SLEEVES.map(({ key, label, color, exposureKey }) => {
    const ps        = positions.filter(p => p.sleeve === key)
    const value     = ps.reduce((s, p) => s + p.market_value, 0)
    const pnl       = ps.reduce((s, p) => s + p.unrealized_pnl, 0)
    const exposure  = snapshot?.[exposureKey] ?? 0
    const qualified = ps.filter(p => latestSignals[p.ticker]?.trend_qual).length
    return { key, label, color, ps, value, pnl, exposure, qualified }
  })

  return (
    <div className="la-grid">
      {/* ── Sleeve columns ── */}
      {sleeveData.map(({ key, label, color, ps, value, pnl, exposure, qualified }) => (
        <div key={key} className="la-col card">
          {/* Header */}
          <div className="la-col-header" style={{ borderBottomColor: color }}>
            <span className="la-col-title" style={{ color }}>{label}</span>
            <span className="la-badge" style={{ background: `color-mix(in srgb, ${color} 15%, transparent)`, color }}>
              {fmtPct(exposure)} deployed
            </span>
          </div>

          {/* Summary */}
          <div className="la-summary">
            <div className="la-summary-stat">
              <span className="la-stat-label">Value</span>
              <span className="la-stat-value">{fmt$(value)}</span>
            </div>
            <div className="la-summary-stat">
              <span className="la-stat-label">P&amp;L</span>
              <span className={`la-stat-value ${pnl >= 0 ? 'positive' : 'negative'}`}>
                {pnl >= 0 ? '+' : ''}{fmt$(pnl)}
              </span>
            </div>
            <div className="la-summary-stat">
              <span className="la-stat-label">Signals</span>
              <span className="la-stat-value">{qualified}/{ps.length} ✓</span>
            </div>
          </div>

          {/* Position rows */}
          <div className="la-positions">
            {ps.length === 0 ? (
              <div className="la-empty">No positions</div>
            ) : (
              [...ps]
                .sort((a, b) => b.market_value - a.market_value)
                .map(p => {
                  const sig  = latestSignals[p.ticker]
                  const qual = sig?.trend_qual
                  const dotColor = qual ? 'var(--positive)' : sig ? 'var(--negative)' : 'var(--neutral)'
                  return (
                    <div key={p.ticker} className="la-pos-row">
                      <div className="la-pos-left">
                        <span className="la-dot" style={{ background: dotColor }} />
                        <span className="la-ticker">{p.ticker}</span>
                        <span className="la-pos-weight">{p.position_pct.toFixed(1)}%</span>
                      </div>
                      <div className="la-pos-right">
                        <span className="la-pos-val">{fmt$(p.market_value)}</span>
                        <span className={`la-pos-pnl ${p.unrealized_pnl >= 0 ? 'positive' : 'negative'}`}>
                          {p.unrealized_pnl >= 0 ? '+' : ''}{fmt$(p.unrealized_pnl)}
                        </span>
                      </div>
                    </div>
                  )
                })
            )}
          </div>
        </div>
      ))}

      {/* ── Overview column ── */}
      <div className="la-col la-overview card">
        <div className="la-col-header" style={{ borderBottomColor: 'var(--accent-cyan)' }}>
          <span className="la-col-title" style={{ color: 'var(--accent-cyan)' }}>Overview</span>
        </div>

        <div className="la-overview-metrics">
          <div className="la-ov-metric">
            <span className="la-stat-label">Net Worth</span>
            <span className="la-ov-value">{fmt$(snapshot?.net_worth ?? 0)}</span>
          </div>
          <div className="la-ov-metric">
            <span className="la-stat-label">{(snapshot?.trading_days_gap ?? 1) > 1 ? `P&L (${snapshot?.trading_days_gap}d)` : 'Daily P&L'}</span>
            <span className={`la-ov-value ${(snapshot?.daily_pnl ?? 0) >= 0 ? 'positive' : 'negative'}`}>
              {(snapshot?.daily_pnl ?? 0) >= 0 ? '+' : ''}{fmt$(snapshot?.daily_pnl ?? 0)}
              <span className="la-ov-sub"> {fmtPct(snapshot?.daily_pnl_pct ?? 0, true)}</span>
            </span>
          </div>
          <div className="la-ov-metric">
            <span className="la-stat-label">Drawdown</span>
            <span className={`la-ov-value ${(snapshot?.drawdown_pct ?? 0) > 5 ? 'negative' : (snapshot?.drawdown_pct ?? 0) > 2 ? 'warning' : ''}`}>
              {fmtPct(snapshot?.drawdown_pct ?? 0)}
            </span>
          </div>
          <div className="la-ov-metric">
            <span className="la-stat-label">Total Deployed</span>
            <span className="la-ov-value">{fmtPct(snapshot?.total_exposure_pct ?? 0)}</span>
          </div>
          <div className="la-ov-metric">
            <span className="la-stat-label">Cash</span>
            <span className="la-ov-value">{fmt$(snapshot?.cash ?? 0)}</span>
          </div>
        </div>

        <div className="la-divider" />

        {/* Signal health bar */}
        <div className="la-health">
          <div className="la-stat-label" style={{ marginBottom: 8 }}>Signal Health</div>
          <div className="la-health-track">
            <div className="la-health-fill" style={{ width: `${healthPct * 100}%`, background: healthColor }} />
          </div>
          <div className="la-health-label">{totalQualified} / {totalSignals} qualified</div>
        </div>

        <div className="la-divider" />

        {/* Sleeve breakdown */}
        <div>
          <div className="la-stat-label" style={{ marginBottom: 8 }}>Sleeve Breakdown</div>
          {sleeveData.map(({ key, label, color, value, exposure }) => (
            <div key={key} className="la-breakdown-row">
              <div className="la-breakdown-left">
                <span className="la-dot la-dot-sm" style={{ background: color }} />
                <span className="la-breakdown-label">{label}</span>
              </div>
              <div className="la-breakdown-right">
                <span className="la-breakdown-pct">{fmtPct(exposure)}</span>
                <span className="la-breakdown-val">{fmt$(value)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
